/**
 * Pushes a batch of presigned parts to R2: a few at a time, each retried on its own.
 *
 * Knows nothing about the CensorMe API — it is handed a file, a part size and a list of
 * `{partNumber, url}`, and resolves when every one of them is in R2. Fetching the next batch and
 * calling `/complete` belong to `audioThunks`, which is where the API lives.
 *
 * Two numbers are the whole design:
 *  - **Concurrency 4.** One part at a time leaves most of a connection idle on the round trips;
 *    many at once divides the same bandwidth and makes each part's progress meaningless. Four
 *    keeps the pipe full without the browser's own six-per-origin cap queueing them anyway.
 *  - **Four attempts per part.** A part is one `partSize` chunk — cheap to redo, and the reason
 *    the file is cut up at all. Losing a 2 GB upload because one 16 MiB chunk hit a blip is
 *    exactly the failure this replaced.
 */

import {AudioUploadPart} from '../backend-types/transcribe-api';
import {putPartToPresignedUrl, R2UploadError} from './r2Upload';

const CONCURRENCY = 4;
const MAX_ATTEMPTS = 4;
const BASE_BACKOFF_MS = 500;

/** The slice a 1-based part number covers. The last part is short; every other one is exact. */
export function sliceForPart(file: File, partSize: number, partNumber: number): Blob {
  const start = (partNumber - 1) * partSize;
  return file.slice(start, Math.min(start + partSize, file.size));
}

/** Bytes a set of parts accounts for, so progress can jump by the right amount when one lands. */
export function bytesInParts(file: File, partSize: number, parts: AudioUploadPart[]): number {
  return parts.reduce((total, part) => total + sliceForPart(file, partSize, part.partNumber).size, 0);
}

/**
 * Sleeps, but wakes early on abort — the longest backoff here is over five seconds, and a Cancel
 * that appears to do nothing for that long reads as a hung upload.
 */
function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(finish, ms);
    function finish() {
      clearTimeout(timer);
      signal?.removeEventListener('abort', finish);
      resolve();
    }
    signal?.addEventListener('abort', finish);
  });
}

/** Exponential with jitter, so a batch that failed together does not retry in lockstep. */
function backoffFor(attempt: number): number {
  return BASE_BACKOFF_MS * 2 ** (attempt - 1) * (1 + Math.random() * 0.3);
}

export interface UploadPartBatchOptions {
  file: File;
  /** Exactly what the server returned. Re-chunking differently corrupts the object silently. */
  partSize: number;
  parts: AudioUploadPart[];
  /** Total bytes of this batch transferred so far, summed across everything in flight. */
  onBatchProgress?: (loadedInBatch: number) => void;
  signal?: AbortSignal;
}

/**
 * Resolves only when every part in the batch is in R2, so a caller that gets past this can count
 * the whole batch as uploaded. Rejects with the first part that ran out of attempts.
 */
export async function uploadPartBatch({
  file,
  partSize,
  parts,
  onBatchProgress,
  signal,
}: UploadPartBatchOptions): Promise<void> {
  if (parts.length === 0) {
    return;
  }

  // Per-part byte counts, summed on every progress event. A retry resets its own entry rather than
  // double-counting the bytes the failed attempt had already reported.
  const loadedByPart = new Map<number, number>();
  const report = () => {
    if (!onBatchProgress) return;
    let total = 0;
    loadedByPart.forEach((loaded) => { total += loaded; });
    onBatchProgress(total);
  };

  let nextIndex = 0;

  const runOnePart = async (part: AudioUploadPart) => {
    const body = sliceForPart(file, partSize, part.partNumber);

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      try {
        loadedByPart.set(part.partNumber, 0);
        await putPartToPresignedUrl({
          url: part.url,
          body,
          signal,
          onProgress: (loaded) => {
            loadedByPart.set(part.partNumber, loaded);
            report();
          },
        });
        loadedByPart.set(part.partNumber, body.size);
        report();
        return;
      } catch (error) {
        loadedByPart.set(part.partNumber, 0);
        report();

        // A cancel is not a failure to retry — it is the user, and every other worker is about to
        // see the same abort.
        if (error instanceof R2UploadError && error.aborted) {
          throw error;
        }
        if (attempt === MAX_ATTEMPTS) {
          throw error;
        }

        await wait(backoffFor(attempt), signal);

        if (signal?.aborted) {
          throw new R2UploadError('Upload cancelled', 0, true);
        }
      }
    }
  };

  // The first part to run out of attempts is the one reported, but the others are drained rather
  // than left to reject into nothing — an unawaited rejection here is an unhandled promise, and a
  // worker that kept pulling would go on uploading parts of an upload that has already failed.
  let firstError: unknown = null;

  // Workers pull from a shared cursor rather than being handed a fixed slice each, so one slow
  // part does not leave three workers idle at the end of the batch.
  const worker = async () => {
    for (;;) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= parts.length || firstError !== null) {
        return;
      }
      try {
        await runOnePart(parts[index]);
      } catch (error) {
        firstError ??= error;
        return;
      }
    }
  };

  await Promise.all(Array.from({length: Math.min(CONCURRENCY, parts.length)}, worker));

  if (firstError !== null) {
    throw firstError;
  }
}
